import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, Popover, Spin, Tree } from 'antd';
import {
	CloseOutlined,
	DownOutlined,
	FilterOutlined,
	HolderOutlined,
	LoadingOutlined,
} from '@ant-design/icons';
import ReactDragListView from 'react-drag-listview';
import OWSearchInput from '../common/OWSearchInput';
import { LocationIdentity } from './LocationColumnHeader';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { cn } from '../../utils/ClassNamesUtils';

// A node with no children is a location; anything above it is a region or sub-region. Same rule
// LocationsTreeSelect applies, and the reason only leaf keys ever become columns.
const isLocationNode = (node) => nullSafeGetOrElse('children', node, []).length === 0;

// Region ids and location ids come from different tables and can collide, so tree keys are prefixed
// and the location id is carried alongside.
const LOCATION_KEY_PREFIX = 'loc-';
const REGION_KEY_PREFIX = 'region-';

const matchesName = (node, needle) =>
	!needle || nullSafeGetOrElse('name', node, '').toLowerCase().indexOf(needle) >= 0;

const buildTreeData = (nodes, needle) =>
	(nodes || []).reduce((acc, node) => {
		const nodeId = nullSafeGet('id', node);
		if (!nodeId) {
			return acc;
		}
		if (isLocationNode(node)) {
			return matchesName(node, needle)
				? acc.concat({
						key: `${LOCATION_KEY_PREFIX}${nodeId}`,
						locationId: nodeId,
						title: <LocationIdentity location={node} widthClassName="w-full" />,
				  })
				: acc;
		}
		// A region survives a search if it still has a matching descendant, so a match is never
		// orphaned from the region it sits under. A region matching by its own name keeps everything
		// beneath it.
		const children = buildTreeData(
			nullSafeGetOrElse('children', node, []),
			matchesName(node, needle) ? '' : needle
		);
		if (children.length === 0) {
			return acc;
		}
		return acc.concat({
			key: `${REGION_KEY_PREFIX}${nodeId}`,
			title: <LocationIdentity location={node} widthClassName="w-full" />,
			selectable: false,
			children,
		});
	}, []);

// Region keys, so the tree can start fully expanded and reopen itself around a search hit.
const collectRegionKeys = (nodes) =>
	(nodes || []).reduce(
		(acc, node) => (node.locationId ? acc : acc.concat(node.key, collectRegionKeys(node.children))),
		[]
	);

const collectLocationIds = (nodes) =>
	(nodes || []).reduce(
		(acc, node) =>
			node.locationId ? acc.concat(node.locationId) : acc.concat(collectLocationIds(node.children)),
		[]
	);

const moveArrayItem = (list: any[], fromIndex: number, toIndex: number): any[] => {
	const result = Array.from(list || []);
	if (fromIndex < 0 || fromIndex >= result.length || toIndex < 0 || toIndex >= result.length) {
		return result;
	}
	const [movedItem] = result.splice(fromIndex, 1);
	result.splice(toIndex, 0, movedItem);
	return result;
};

interface LocationFilterPopoverProps {
	/** Ordered ids — the order is the column order. */
	selectedLocationIds: any[];
	onChange: (orderedLocationIds: any[]) => void;
	/**
	 * The flat set of locations the buyer can reach. Backs "All locations" and resolves a selected id
	 * to a record for the column-order rows.
	 */
	knownLocations: any[];
	/**
	 * Regions with their locations nested underneath, as the hierarchy endpoint returns it. Passed in
	 * rather than read from redux: that endpoint writes region records into the same `records` map as
	 * locations, where the two id spaces can collide.
	 */
	locationTree: any[];
	/** Column count when nothing is selected, so the label matches what the calendar shows. */
	defaultCount?: number;
	usingDefaultSelection?: boolean;
	/** The locations fetch is still in flight, so there is nothing to list yet. */
	fetching?: boolean;
	/** Locations are still loading, so the control cannot be used yet. */
	loading?: boolean;
}

export const LocationFilterPopover: FC<LocationFilterPopoverProps> = ({
	selectedLocationIds,
	onChange,
	knownLocations,
	locationTree,
	defaultCount = 0,
	usingDefaultSelection = false,
	fetching = false,
	loading = false,
}) => {
	const [visible, setVisible] = useState(false);
	const [searchText, setSearchText] = useState('');
	const [expandedKeys, setExpandedKeys] = useState<any[]>([]);
	const [autoExpandParent, setAutoExpandParent] = useState(true);

	// Memoized: `|| []` would otherwise be a new array on every render and re-run everything below it.
	const selectedIds = useMemo(() => selectedLocationIds || [], [selectedLocationIds]);

	const locationsById = useMemo(() => {
		const lookup = {};
		(knownLocations || []).forEach((location) => {
			const locationId = nullSafeGet('id', location);
			if (locationId) {
				lookup[locationId] = location;
			}
		});
		return lookup;
	}, [knownLocations]);

	// A selected id with no record yet still gets a row, so removing it never waits on the fetch.
	const orderedLocations = useMemo(
		() =>
			selectedIds.map(
				(locationId) => locationsById[locationId] || { id: locationId, name: `#${locationId}` }
			),
		[selectedIds, locationsById]
	);
	const hasColumnOrder = orderedLocations.length > 0;

	const treeData = useMemo(
		() => buildTreeData(locationTree, `${searchText || ''}`.trim().toLowerCase()),
		[locationTree, searchText]
	);

	// Expansion is controlled, the way InlineTreeFilter does it: uncontrolled, the tree loses its
	// open branches whenever the checked set changes. Everything starts open, and a new search
	// reopens around what it found.
	useEffect(() => {
		setExpandedKeys(collectRegionKeys(treeData));
		setAutoExpandParent(true);
	}, [treeData]);

	const handleExpand = useCallback((keys) => {
		setExpandedKeys(keys);
		setAutoExpandParent(false);
	}, []);

	// Ticking a region ticks its locations, so the checked set is the selected leaves plus every
	// region whose leaves are all selected.
	const checkedKeys = useMemo(() => {
		const selected = new Set(selectedIds.map((locationId) => `${locationId}`));
		const keys = [];
		const walk = (nodes) => {
			let allChecked = (nodes || []).length > 0;
			(nodes || []).forEach((node) => {
				if (node.locationId) {
					if (selected.has(`${node.locationId}`)) {
						keys.push(node.key);
					} else {
						allChecked = false;
					}
					return;
				}
				if (walk(node.children)) {
					keys.push(node.key);
				} else {
					allChecked = false;
				}
			});
			return allChecked;
		};
		walk(treeData);
		return keys;
	}, [treeData, selectedIds]);

	// The tree hands back its whole checked set with no ordering. Keep the order of what is already
	// selected and append what is new, so a tick never scrambles the column order. Ids hidden by the
	// current search are left alone rather than dropped.
	const handleCheck = useCallback(
		(checked) => {
			const checkedList = Array.isArray(checked)
				? checked
				: nullSafeGetOrElse('checked', checked, []);
			const nextIds = new Set(
				checkedList
					.filter((key) => `${key}`.indexOf(LOCATION_KEY_PREFIX) === 0)
					.map((key) => `${key}`.slice(LOCATION_KEY_PREFIX.length))
			);
			const listedIds = collectLocationIds(treeData);
			const isListed = (locationId) => listedIds.some((listed) => `${listed}` === `${locationId}`);
			const untouched = selectedIds.filter((locationId) => !isListed(locationId));
			const kept = selectedIds.filter(
				(locationId) => isListed(locationId) && nextIds.has(`${locationId}`)
			);
			const added = listedIds.filter(
				(locationId) =>
					nextIds.has(`${locationId}`) &&
					!kept.some((keptId) => `${keptId}` === `${locationId}`) &&
					!untouched.some((untouchedId) => `${untouchedId}` === `${locationId}`)
			);
			onChange(untouched.concat(kept, added));
		},
		[selectedIds, onChange, treeData]
	);

	const allLocationIds = useMemo(
		() => (knownLocations || []).map((location) => nullSafeGet('id', location)).filter(Boolean),
		[knownLocations]
	);
	const allSelected =
		allLocationIds.length > 0 &&
		allLocationIds.every((locationId) => selectedIds.indexOf(locationId) >= 0);
	const someSelected = allLocationIds.some((locationId) => selectedIds.indexOf(locationId) >= 0);

	const handleToggleAll = useCallback(() => {
		onChange(allSelected ? [] : allLocationIds);
	}, [allSelected, allLocationIds, onChange]);

	const handleClearAll = useCallback(() => onChange([]), [onChange]);
	const handleClose = useCallback(() => setVisible(false), []);
	const handleSearchChange = useCallback((value) => setSearchText(value || ''), []);

	const createRemoveHandler = (locationId) => () =>
		onChange(selectedIds.filter((selectedId) => selectedId !== locationId));

	const dragProps = useMemo(
		() => ({
			nodeSelector: '.locationOrderRow',
			handleSelector: '.locationOrderHandle',
			onDragEnd(fromIndex, toIndex) {
				onChange(moveArrayItem(selectedIds, fromIndex, toIndex));
			},
		}),
		[selectedIds, onChange]
	);

	const content = (
		<div className="calendarFilterPopover">
			<div className="flex flex-row items-center justify-between gap-3 border-0 border-b border-solid border-grey-5 p-4">
				<span className="h5medium text-character-primary">Locations</span>
				<div className="flex flex-row items-center gap-2">
					<Button type="link" size="small" onClick={handleClearAll}>
						Clear all
					</Button>
					<Button type="text" size="small" icon={<CloseOutlined />} onClick={handleClose} />
				</div>
			</div>
			<div className="flex flex-row">
				<div className="flex w-[320px] flex-col gap-3 p-4">
					<OWSearchInput
						placeholder="Search locations"
						onChange={handleSearchChange}
						style={{ width: '100%' }}
					/>
					<div className="max-h-[300px] overflow-y-auto">
						{fetching && treeData.length === 0 ? (
							<div className="flex flex-row justify-center py-6">
								<Spin />
							</div>
						) : treeData.length === 0 ? (
							<div className="bodyregular py-6 text-center text-character-secondary">
								No locations found
							</div>
						) : (
							<div className="flex flex-col gap-2">
								<div
									className="footnoteregular flex cursor-pointer flex-row items-center gap-2 border-0 border-b border-solid border-grey-5 py-1 uppercase text-character-secondary"
									onClick={handleToggleAll}
								>
									<Checkbox
										className="flex-none"
										checked={allSelected}
										indeterminate={!allSelected && someSelected}
									/>
									<span>All locations</span>
								</div>
								<Tree
									checkable
									blockNode
									selectable={false}
									treeData={treeData}
									checkedKeys={checkedKeys}
									expandedKeys={expandedKeys}
									autoExpandParent={autoExpandParent}
									onExpand={handleExpand}
									onCheck={handleCheck}
								/>
							</div>
						)}
					</div>
				</div>
				{/* Stays mounted so it can slide in and out; the outer track collapses its width while the
				    inner panel keeps its own, so the content never reflows mid-animation. */}
				<div
					className={cn(
						'overflow-hidden transition-[width] duration-300 ease-out',
						hasColumnOrder ? 'w-[320px]' : 'w-0'
					)}
				>
					<div
						className={cn(
							'flex w-[320px] flex-col gap-2 border-0 border-l border-solid border-grey-4 bg-grey-2 p-4 transition-transform duration-300 ease-out h-full',
							hasColumnOrder ? 'translate-x-0' : 'translate-x-full'
						)}
					>
						<span className="footnoteregular border-0 border-b border-solid border-grey-5 py-1 uppercase text-character-secondary">
							Column order
						</span>
						<div className="max-h-[360px] overflow-y-auto">
							<ReactDragListView {...dragProps}>
								<div className="flex flex-col gap-2">
									{orderedLocations.map((location) => {
										const locationId = nullSafeGet('id', location);
										return (
											<div
												key={locationId}
												className="locationOrderRow flex flex-row items-center gap-2"
											>
												<HolderOutlined className="locationOrderHandle flex-none cursor-move text-grey-7" />
												<LocationIdentity location={location} />
												<Button
													type="text"
													size="small"
													className="flex-none"
													icon={<CloseOutlined />}
													onClick={createRemoveHandler(locationId)}
												/>
											</div>
										);
									})}
								</div>
							</ReactDragListView>
						</div>
					</div>
				</div>
			</div>
		</div>
	);

	return (
		<Popover
			trigger="click"
			placement="bottomLeft"
			visible={visible}
			onVisibleChange={setVisible}
			content={content}
			overlayClassName="calendarFilterPopover__overlay"
		>
			<Button className="flex flex-row items-center gap-1" disabled={loading}>
				{loading ? <LoadingOutlined /> : <FilterOutlined />}
				<span>Locations ({usingDefaultSelection ? defaultCount : selectedIds.length})</span>
				<DownOutlined className="text-xs" />
			</Button>
		</Popover>
	);
};
